import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { Trans, useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:last-child td, &:last-child th': {
    border: 0
  }
}))

// Data structure
const QNA_DATA = [
  'color',
  'religion',
  'happiness',
  'netflix',
  '21st',
  'eat',
  'food',
  'love',
  'hardestMoment',
  'worried',
  'wish',
  'important',
  'scariest',
  'hotpot',
  'confidence',
  'drone',
  'regret',
  'phone',
  'wallpaper',
  'book',
  'fragrance',
  'mavic',
  'born',
  'callORtext',
  'summerORwinter',
  'mountainORsea',
  'alcoholORcoffee',
  'Naeng',
  'beefORpork',
  'meatORkimchi',
  'pizzaORchicken',
  'kimichORsoy',
  'goodAt',
  'peopleTalk',
  'makeHappy',
  'makeCry',
  'fruit',
  'nickname',
  'cool'
]

// Helper function to create table data
const createTableData = (t, dataKeys) => {
  return dataKeys.map(key => ({
    question: t(`qna:${key}.q`),
    answer: t(`qna:${key}.a`)
  }))
}

// Table Header Component
const TableHeader = ({ t }) => (
  <TableHead>
    <TableRow>
      <StyledTableCell>{t('qna:question')}</StyledTableCell>
      <StyledTableCell>{t('qna:answer')}</StyledTableCell>
    </TableRow>
  </TableHead>
)

// Table Body Component
const TableBodyContent = ({ rows }) => (
  <TableBody>
    {rows.map((row) => (
      <StyledTableRow key={row.question}>
        <StyledTableCell component="th" scope="row">
          {row.question}
        </StyledTableCell>
        <StyledTableCell>{row.answer}</StyledTableCell>
      </StyledTableRow>
    ))}
  </TableBody>
)

// Toggle Button Component
const ToggleButton = ({ show, onToggle, t }) => (
  <Box sx={{ textAlign: 'left' }}>
    <Button
      variant="contained"
      size="small"
      color="primary"
      onClick={onToggle}
      startIcon={<RemoveRedEyeIcon />}
      sx={{ fontSize: '10px' }}
    >
      {t('qna:moreQuestions')}
    </Button>
  </Box>
)

// Comment Component
const Comment = ({ t }) => (
  <Typography
    paragraph
    sx={{ mt: 2, fontSize: 'small', textAlign: 'left' }}
    variant="subtitle2"
  >
    <Trans t={t} i18nKey="qna:comment">
      Please don't take it too seriously. <em>It's just for fun. </em>
    </Trans>
  </Typography>
)

// Main QnATable Component
export default function QnATable({ 
  dataKeys = QNA_DATA,
  buttonColor = 'error',
  tableSize = 'small',
  showComment = true
}) {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  const rows = createTableData(t, dataKeys)

  const handleToggle = () => setShow(prev => !prev)

  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Box sx={{ mb: 2, width: '100%' }}>
        <ToggleButton 
          show={show} 
          onToggle={handleToggle} 
          t={t} 
        />
        
        {show && (
          <>
            {showComment && <Comment t={t} />}
            
            <TableContainer component={Paper}>
              <Table size={tableSize} aria-label="customized table">
                <TableHeader t={t} />
                <TableBodyContent rows={rows} />
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </Box>
  )
}
